import { useEffect, useRef, useState } from 'react';
import useHoverOnScroll from '../hooks/useHoverOnScroll';
import useWheelScroll from '../hooks/useWheelScroll';
import useEntryStagger from '../hooks/useEntryStagger';
import useIsMobile from '../hooks/useIsMobile';
import { matchesCategory } from '../data/portfolio';
import {
    FADE_MASK,
    MOBILE_FADE_MASK,
    PANEL_TOP,
    PANEL_BOTTOM,
    LIST_PAD_TOP,
    LIST_PAD_BOTTOM,
    ROW_PAD_Y,
    MOBILE_EDGE,
    MOBILE_PANEL_LEFT,
    MOBILE_PANEL_TOP,
    MOBILE_PANEL_BOTTOM,
    MOBILE_LIST_PAD,
    MOBILE_ROW_PAD_Y,
} from '../theme';

const BADGE_PILL = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '.08em',
    textTransform: 'uppercase',
    padding: '5px 9px',
    borderRadius: '3px',
    textDecoration: 'none',
    pointerEvents: 'auto',
    cursor: 'default',
    transition: 'background .25s ease, border-color .25s ease, opacity .25s ease',
};

const ONGOING_BADGE = {
    label: 'Ongoing',
    style: {
        ...BADGE_PILL,
        border: '1px solid color-mix(in oklab, currentColor 18%, transparent)',
        color: 'inherit',
        opacity: 0.9,
    },
    dot: {
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        background: 'var(--accent)',
        boxShadow: '0 0 8px var(--accent)',
        animation: 'sigPulse 2s ease-in-out infinite',
        flex: '0 0 auto',
    },
};

function accessBadge(project) {
    if (project.access === 'repo') {
        return {
            href: project.link,
            // Not every link is a repo, a live site shouldn't be labelled GitHub.
            label: project.link.startsWith('/blog/') ? 'Read the story ↗' : project.link.includes('github.com') ? 'GitHub ↗' : 'Visit ↗',
            style: {
                ...BADGE_PILL,
                cursor: 'pointer',
                border: '1px solid color-mix(in oklab, var(--accent) 55%, transparent)',
                color: 'var(--accent)',
            },
        };
    }
    if (project.access === 'public') {
        return {
            href: null,
            label: 'Public',
            style: {
                ...BADGE_PILL,
                border: '1px solid color-mix(in oklab, currentColor 18%, transparent)',
                color: 'inherit',
                opacity: 0.75,
            },
        };
    }
    return {
        href: null,
        label: 'Private',
        style: {
            ...BADGE_PILL,
            border: '1px dashed color-mix(in oklab, currentColor 24%, transparent)',
            color: 'inherit',
            opacity: 0.5,
        },
    };
}

function ProjectRow({
    project,
    index,
    isOpen,
    isHover,
    dim,
    shown,
    staggered,
    onToggle,
    onEnter,
    onLeave,
    palette,
    mobile,
}) {
    const access = accessBadge(project);
    const AccessTag = access.href ? 'a' : 'span';
    // Still going beats how visible it is: an unfinished public project reads as
    // ongoing, which is the more useful thing to know at a glance.
    const marker = project.ongoing
        ? palette.marker.ongoing
        : project.access === 'private'
            ? palette.marker.private
            : palette.marker.done;

    return (
        <div
            className="sig-project-row"
            data-row-id={project.id}
            style={{
                padding: `${mobile ? MOBILE_ROW_PAD_Y : ROW_PAD_Y}px 0`,
                opacity: shown ? (dim ? 0.65 : 1) : 0,
                transform: shown ? 'translateY(0)' : 'translateY(10px)',
                transition: 'opacity .5s ease, transform .5s ease',
                // Stagger only on entry, never while moving between rows.
                transitionDelay: staggered ? `${(index * 0.05).toFixed(2)}s` : '0s',
            }}
        >
            <div
                style={{
                    fontFamily: "'Neue Montreal',sans-serif",
                    fontWeight: 300,
                    // Shared heading scale; long titles wrap within the reading column.
                    fontSize: mobile ? '24px' : 'clamp(26px,2.8vw,40px)',
                    lineHeight: 1.08,
                    letterSpacing: '.005em',
                    whiteSpace: 'normal',
                    // Colour alone marks the lit row: a rule under the title only
                    // pushed the type/date line away from the name it belongs to.
                    color: isHover || isOpen ? 'var(--accent)' : 'inherit',
                    transition: 'color .25s ease',
                }}
            >
                <button
                    type="button"
                    aria-expanded={isOpen}
                    // The entire title row is an accessible expand control.
                    onClick={onToggle}
                    onMouseEnter={mobile ? undefined : onEnter}
                    onMouseLeave={mobile ? undefined : onLeave}
                    data-hover-id={project.id}
                    style={{ cursor: 'pointer', background: 'none', border: 0, padding: mobile ? '16px 0 0' : 0,
                        color: 'inherit', font: 'inherit', letterSpacing: 'inherit', textAlign: 'inherit',
                        minHeight: mobile ? '44px' : undefined, width: '100%',
                        display: 'flex', alignItems: 'baseline', justifyContent: mobile ? 'flex-start' : 'flex-end', gap: '8px' }}
                >
                    <span
                        style={{
                            // Align the numeral with the bottom baseline of the title.
                            fontSize: '0.3em',
                            fontWeight: 400,
                            letterSpacing: '.05em',
                            flexShrink: 0,
                            opacity: 0.45,
                        }}
                    >
                        {index + 1}
                    </span>
                    <span>{project.name}</span>
                </button>
            </div>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: mobile ? 'flex-start' : 'flex-end',
                    gap: '7px',
                    fontSize: '11px',
                    fontWeight: 700,
                    letterSpacing: '.05em',
                    // Negative: the title's line box carries descender space the
                    // meta line can sit inside, so it tucks under the name.
                    marginTop: '0px',
                    opacity: mobile ? 0.72 : 0.6,
                }}
            >
                <span
                    style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        flex: '0 0 auto',
                        background: marker,
                        boxShadow: project.access === 'private' ? 'none' : `0 0 6px ${marker}`,
                        transition: 'background .25s ease, box-shadow .25s ease',
                    }}
                />
                <span>
                    {project.cat} / {project.date}
                </span>
            </div>

            <div
                inert={!isOpen}
                aria-hidden={!isOpen}
                style={{ display: 'grid', gridTemplateRows: isOpen ? '1fr' : '0fr', opacity: isOpen ? 1 : 0, marginTop: isOpen ? '18px' : 0, transition: 'grid-template-rows .4s ease, opacity .4s ease, margin-top .4s ease' }}
            >
                {/* A grid track animates natural content height without a fixed cap. */}
                <div style={{ minHeight: 0, overflow: 'hidden' }}>
                {project.desc && (
                    <div
                        style={{
                            maxWidth: mobile ? 'none' : '440px',
                            marginLeft: mobile ? 0 : 'auto',
                            textAlign: mobile ? 'left' : 'right',
                            fontSize: mobile ? '15px' : '14px',
                            lineHeight: 1.62,
                            fontFamily: "'Hanken Grotesk',sans-serif",
                            color: 'inherit',
                            opacity: 0.82,
                        }}
                    >
                        {project.desc}
                    </div>
                )}
                {project.links.length > 0 && (
                    <div
                        style={{
                            maxWidth: mobile ? 'none' : '440px',
                            marginLeft: mobile ? 0 : 'auto',
                            marginTop: project.desc ? '10px' : '0px',
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: mobile ? 'flex-start' : 'flex-end',
                            gap: '6px 16px',
                            fontSize: '12px',
                        }}
                    >
                        {project.links.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="sig-link"
                                // Without this the row's own onClick collapses it mid-navigation.
                                onClick={(e) => e.stopPropagation()}
                            >
                                {l.label}
                            </a>
                        ))}
                    </div>
                )}
                {project.note && (
                    <div
                        style={{
                            maxWidth: mobile ? 'none' : '440px',
                            marginLeft: mobile ? 0 : 'auto',
                            marginTop: project.desc ? '9px' : '0px',
                            textAlign: mobile ? 'left' : 'right',
                            fontFamily: "'Hanken Grotesk',sans-serif",
                            // Italic and dimmer than the description, a caveat about the
                            // link, not part of the project's own account of itself.
                            fontStyle: 'italic',
                            fontSize: '12px',
                            lineHeight: 1.55,
                            opacity: 0.7,
                        }}
                    >
                        {project.note}
                    </div>
                )}
                <div
                    style={{
                            maxWidth: mobile ? 'none' : '440px',
                            marginLeft: mobile ? 0 : 'auto',
                        marginTop: project.desc || project.note ? '14px' : '0px',
                        display: 'flex',
                            justifyContent: mobile ? 'flex-start' : 'flex-end',
                            flexWrap: 'wrap',
                        gap: '10px',
                    }}
                >
                    {project.pill && (
                        <span
                            style={{
                                ...BADGE_PILL,
                                // Faintly filled rather than outlined: enough to separate a
                                // context label from the status pills beside it, but calm
                                // enough to repeat down most of the list.
                                border: '1px solid color-mix(in oklab, var(--accent) 28%, transparent)',
                                background: 'color-mix(in oklab, var(--accent) 8%, transparent)',
                                color: 'var(--accent)',
                                opacity: 0.85,
                            }}
                        >
                            {project.pill}
                        </span>
                    )}
                    {project.ongoing && (
                        <span style={ONGOING_BADGE.style}>
                            <span style={ONGOING_BADGE.dot} />
                            {ONGOING_BADGE.label}
                        </span>
                    )}
                    <AccessTag
                        className={access.href ? 'sig-badge' : undefined}
                        href={access.href || undefined}
                        target={access.href && !access.href.startsWith('/') ? '_blank' : undefined}
                        rel={access.href && !access.href.startsWith('/') ? 'noopener noreferrer' : undefined}
                        // Keep a link click from also collapsing the row.
                        onClick={access.href ? (e) => e.stopPropagation() : undefined}
                        style={access.style}
                    >
                        {access.label}
                    </AccessTag>
                </div>
                </div>
            </div>
        </div>
    );
}

const FILTER_FADE_MS = 220;

const NO_HOVER = () => {};

export default function ProjectsPanel({
    projects,
    category,
    open,
    onToggle,
    onClose,
    shown,
    palette,
}) {
    // Rows leaving on a filter change cannot be transitioned once React has
    // unmounted them, so the swap is deferred: fade out against the category still
    // being shown, exchange the rows while invisible, then fade back in.
    const [shownCategory, setShownCategory] = useState(category);
    const [fading, setFading] = useState(false);
    const [hover, setHover] = useState(null);
    const staggered = useEntryStagger(shown, hover);
    const mobile = useIsMobile();

    // Drop the hover when the panel leaves, so returning to it doesn't come back
    // with a stale row lit and the rest dimmed.
    useEffect(() => {
        if (!shown) setHover(null);
    }, [shown]);

    // On touch nothing can hover, so hover is never entered on mobile: the list
    // reacts to taps only, and the open row is the sole lit one.
    const trackHover = mobile ? NO_HOVER : setHover;

    const scrollRef = useRef(null);
    useHoverOnScroll(scrollRef, trackHover);
    useWheelScroll(scrollRef, shown);

    // Keep the detail open while reading or selecting text inside its row.
    useEffect(() => {
        if (!shown || open == null) return undefined;
        const onDocClick = (e) => {
            const el = e.target;
            if (el && el.closest && el.closest('[data-row-id]')) return;
            onClose();
        };
        document.addEventListener('click', onDocClick);
        return () => document.removeEventListener('click', onDocClick);
    }, [shown, open, onClose, mobile]);

    useEffect(() => {
        if (category === shownCategory) {
            setFading(false);
            return undefined;
        }
        setFading(true);
        const t = setTimeout(() => {
            setShownCategory(category);
            setFading(false);
        }, FILTER_FADE_MS);
        return () => clearTimeout(t);
    }, [category, shownCategory]);

    const visible = projects.filter((p) => matchesCategory(p, shownCategory));

    // Bring the expanded row into view when it isn't already, arriving from a
    // skill badge can target a project far down the list, which would otherwise
    // look like nothing happened. Rows already in view are left alone so an
    // ordinary click doesn't yank the list around.
    //
    // This fires once per opened project and then stays out of the way: scrolling
    // updates the hover, which re-renders, and re-scrolling on every render would
    // pin the list to the open row and make it impossible to scroll off.
    const scrolledFor = useRef(null);
    useEffect(() => {
        if (!shown || open == null) {
            scrolledFor.current = null;
            return;
        }
        if (scrolledFor.current === open) return;
        const box = scrollRef.current;
        if (!box) return;
        const row = [...box.querySelectorAll('[data-row-id]')].find(
            (el) => el.getAttribute('data-row-id') === open
        );
        // Absent mid filter-swap; shownCategory settling re-runs this.
        if (!row) return;
        scrolledFor.current = open;

        const boxRect = box.getBoundingClientRect();
        const rowRect = row.getBoundingClientRect();
        const inset = 60; // clear of the fade bands at both ends
        if (rowRect.top < boxRect.top + inset || rowRect.bottom > boxRect.bottom - inset) {
            row.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
    }, [shown, open, shownCategory]);

    return (
        <div
            inert={!shown}
            aria-hidden={!shown}
            style={{
                position: 'absolute',
                right: `${mobile ? MOBILE_EDGE : 68}px`,
                left: mobile ? `${MOBILE_PANEL_LEFT}px` : undefined,
                top: mobile ? `calc(${MOBILE_PANEL_TOP}px + env(safe-area-inset-top, 0px))` : `${PANEL_TOP}px`,
                bottom: mobile ? `calc(${MOBILE_PANEL_BOTTOM}px + env(safe-area-inset-bottom, 0px))` : `${PANEL_BOTTOM}px`,
                width: mobile ? 'auto' : 'min(660px,52vw)',
                pointerEvents: shown ? 'auto' : 'none',
                opacity: shown ? 1 : 0,
                transform: shown ? 'translateY(0)' : 'translateY(14px)',
                transition: shown
                    ? 'opacity .55s ease, transform .55s ease'
                    : 'opacity .3s ease, transform .3s ease',
            }}
        >
            <div
                ref={scrollRef}
                className="sig-project-scroll"
                onMouseLeave={() => trackHover(null)}
                style={{
                    height: '100%',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                    paddingRight: mobile ? '0' : '8px',
                    textAlign: mobile ? 'left' : 'right',
                    display: 'flex',
                    flexDirection: 'column',
                    // Fade in fixed px, matched to the content padding below, so the band
                    // always falls on empty space rather than dimming the first/last row.
                    maskImage: mobile ? MOBILE_FADE_MASK : FADE_MASK,
                    WebkitMaskImage: mobile ? MOBILE_FADE_MASK : FADE_MASK,
                }}
            >
                <div
                    style={{
                        margin: mobile ? 0 : 'auto 0',
                        padding: mobile
                            ? `${MOBILE_LIST_PAD}px 0`
                            : `${LIST_PAD_TOP}px 0 ${LIST_PAD_BOTTOM}px`,
                        opacity: fading ? 0 : 1,
                        transform: fading ? 'translateY(8px)' : 'translateY(0)',
                        transition: `opacity ${FILTER_FADE_MS}ms ease, transform ${FILTER_FADE_MS}ms ease`,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {visible.map((p, i) => (
                        <ProjectRow
                            key={p.id}
                            project={p}
                            index={i}
                            isOpen={open === p.id}
                            isHover={hover === p.id}
                            // Opening a row is what pushes the rest back; hovering only
                            // lights the name under the pointer and leaves the list alone.
                            dim={open != null && open !== p.id}
                            shown={shown}
                            staggered={staggered}
                            onToggle={() => onToggle(p.id)}
                            onEnter={() => trackHover(p.id)}
                            onLeave={() => trackHover(null)}
                            palette={palette}
                            mobile={mobile}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}
